<template>
    <section class="dp-layout class-affair-ledger">
        <section class="dp-left">
            <DepartmentPane directory-scope="teaching" pane-title="组织架构" default-select-root @tree-node-click="treeNodeClick"></DepartmentPane>
        </section>
        <section class="dp-right" v-if="schoolId > 0">
            <div class="scope-hint" v-if="scopeLabel">当前：{{ scopeLabel }}</div>
            <el-form :inline="true" size="small" class="toolbar">
                <el-form-item label="时段">
                    <el-select v-model="windowId" clearable placeholder="全部" style="width:240px" @change="load(1)">
                        <el-option v-for="w in windows" :key="w.Id" :label="w.Title || (w.StartDate + '~' + w.EndDate)" :value="w.Id" />
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button
                        v-if="hasActionPermission('handleExport')"
                        type="success"
                        size="small"
                        icon="el-icon-download"
                        :loading="exporting"
                        @click="handleExport"
                    >导出</el-button>
                </el-form-item>
            </el-form>
            <el-table :data="rows" v-loading="loading" border size="small" class="ledger-table">
                <el-table-column prop="ClassName" label="班级" min-width="120" show-overflow-tooltip />
                <el-table-column prop="WindowTitle" label="时段" min-width="140" show-overflow-tooltip />
                <el-table-column label="起止" min-width="180" show-overflow-tooltip>
                    <template slot-scope="scope">{{ scope.row.StartDate }} ~ {{ scope.row.EndDate }}</template>
                </el-table-column>
                <el-table-column prop="TeacherName" label="提交人" min-width="100" show-overflow-tooltip />
                <el-table-column prop="StudentCount" label="人数" min-width="80" align="center" />
                <el-table-column label="状态" min-width="90" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="scope.row.Status === 2 ? 'danger' : 'success'" size="mini">
                            {{ scope.row.StatusName || (scope.row.Status === 2 ? '已作废' : '已提交') }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="SubmitTime" label="提交时间" min-width="160" show-overflow-tooltip />
                <el-table-column label="操作" width="140" align="center">
                    <template slot-scope="scope">
                        <el-button type="text" size="mini" class="op-link" @click="openDetail(scope.row)">明细</el-button>
                        <el-button
                            v-if="scope.row.Status !== 2 && hasActionPermission('openVoidDialog')"
                            type="danger"
                            size="mini"
                            plain
                            @click="openVoidDialog(scope.row)"
                        >作废</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <el-pagination class="pager" background layout="total, prev, pager, next" :current-page="page" :page-size="pageSize" :total="total" @current-change="load" />
        </section>
        <section class="dp-right" v-else><div class="empty-tip">请在左侧选择部门</div></section>

        <el-dialog
            title="德育台账明细"
            :visible.sync="detailVisible"
            width="820px"
            :close-on-click-modal="false"
            append-to-body
            custom-class="dialog-fixed-height class-affair-ledger-dialog"
        >
            <div v-if="detail" class="ledger-detail" :class="{ 'is-voided': detail.Status === 2 }">
                <div class="detail-head">
                    <div class="detail-head__main">
                        <div class="detail-title">
                            <span class="detail-title__class">{{ detail.ClassName || '-' }}</span>
                            <el-tag size="mini" type="info" effect="plain">{{ detail.WindowTitle || '-' }}</el-tag>
                            <el-tag v-if="detail.Status === 2" size="mini" type="danger" effect="dark">已作废</el-tag>
                            <el-tag v-else size="mini" type="success" effect="plain">已提交</el-tag>
                        </div>
                        <div class="detail-sub">
                            <span>{{ detail.StartDate }} ~ {{ detail.EndDate }}</span>
                            <span v-if="detail.TeacherName">· {{ detail.TeacherName }}</span>
                            <span v-if="detail.SubmitTime">· {{ detail.SubmitTime }}</span>
                        </div>
                        <div v-if="detail.Status === 2" class="detail-void-meta">
                            <span>赋分已冲正，以下为作废前快照</span>
                            <span v-if="detail.VoidReason" class="detail-void-meta__reason">作废原因：{{ detail.VoidReason }}</span>
                        </div>
                    </div>
                </div>
                <div class="stat-row">
                    <div class="stat-card">
                        <div class="stat-card__label">学生人数</div>
                        <div class="stat-card__value">{{ detailLines.length }}</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-card__label">日常德育分合计</div>
                        <div class="stat-card__value">{{ formatScore(dailySum) }}</div>
                    </div>
                    <div class="stat-card is-bonus">
                        <div class="stat-card__label">班主任赋分合计</div>
                        <div class="stat-card__value">{{ formatScore(bonusSum) }}</div>
                    </div>
                    <div class="stat-card is-total">
                        <div class="stat-card__label">学期德育分合计</div>
                        <div class="stat-card__value">{{ formatScore(totalSum) }}</div>
                    </div>
                </div>
                <div class="detail-toolbar">
                    <el-input
                        v-model="detailKeyword"
                        size="small"
                        clearable
                        prefix-icon="el-icon-search"
                        placeholder="搜索学生姓名"
                        style="width: 220px"
                        @input="onDetailSearch"
                    />
                    <span class="detail-toolbar__hint">共 {{ filteredDetailLines.length }} 人</span>
                </div>
                <div class="detail-table-wrap">
                    <el-table :data="pagedDetailLines" border size="small" height="100%" class="detail-table">
                        <el-table-column label="#" width="50" align="center">
                            <template slot-scope="scope">{{ detailRowIndex(scope.$index) }}</template>
                        </el-table-column>
                        <el-table-column prop="StudentName" label="学生" min-width="120" show-overflow-tooltip />
                        <el-table-column label="日常德育分" min-width="120" align="center">
                            <template slot-scope="scope">{{ formatScore(scope.row.DailyScore) }}</template>
                        </el-table-column>
                        <el-table-column label="班主任赋分" min-width="120" align="center">
                            <template slot-scope="scope">
                                <span :class="{ 'score-bonus': Number(scope.row.BonusScore) > 0 && detail.Status !== 2 }">{{ formatScore(scope.row.BonusScore) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="学期德育分" min-width="120" align="center">
                            <template slot-scope="scope">
                                <span :class="{ 'score-total': detail.Status !== 2 }">{{ formatScore(scope.row.TotalScore) }}</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
                <div class="detail-pager">
                    <el-pagination
                        background
                        small
                        layout="total, sizes, prev, pager, next"
                        :current-page.sync="detailPage"
                        :page-size.sync="detailPageSize"
                        :page-sizes="[10, 20, 50, 100]"
                        :total="filteredDetailLines.length"
                        @size-change="detailPage = 1"
                    />
                </div>
            </div>
            <span slot="footer">
                <el-button type="primary" size="small" @click="detailVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <el-dialog title="作废台账" :visible.sync="voidVisible" width="460px" :close-on-click-modal="false" append-to-body>
            <p class="void-tip">作废后将冲正本班班主任赋分，并允许班主任在开放时段内重新提交。</p>
            <el-form size="small" label-width="80px">
                <el-form-item label="作废原因">
                    <el-input
                        v-model="voidReason"
                        type="textarea"
                        :rows="3"
                        maxlength="500"
                        show-word-limit
                        placeholder="选填，默认「管理员作废」"
                    />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button size="small" @click="voidVisible = false">取消</el-button>
                <el-button type="danger" size="small" :loading="voiding" @click="confirmVoid">确认作废</el-button>
            </span>
        </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import { getClassAffairLedgerWindows, getClassAffairLedgers, getClassAffairLedgerDetail, exportClassAffairLedgers, voidClassAffairLedger } from '../../api/modules/classAffair';

export default {
    name: 'ClassAffairLedger',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            departmentId: 0,
            scopeLabel: '',
            buttonList: [],
            windows: [],
            windowId: null,
            rows: [],
            loading: false,
            exporting: false,
            page: 1,
            pageSize: 20,
            total: 0,
            detailVisible: false,
            detail: null,
            detailKeyword: '',
            detailPage: 1,
            detailPageSize: 20,
            voidVisible: false,
            voiding: false,
            voidReason: '',
            voidRow: null
        };
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
    },
    computed: {
        detailLines() {
            return (this.detail && this.detail.Lines) || [];
        },
        filteredDetailLines() {
            const kw = (this.detailKeyword || '').trim().toLowerCase();
            if (!kw) return this.detailLines;
            return this.detailLines.filter(row => {
                const name = String(row.StudentName || row.studentName || '').toLowerCase();
                return name.indexOf(kw) >= 0;
            });
        },
        pagedDetailLines() {
            const start = (this.detailPage - 1) * this.detailPageSize;
            return this.filteredDetailLines.slice(start, start + this.detailPageSize);
        },
        dailySum() {
            return this.detailLines.reduce((sum, row) => sum + (Number(row.DailyScore) || 0), 0);
        },
        bonusSum() {
            return this.detailLines.reduce((sum, row) => sum + (Number(row.BonusScore) || 0), 0);
        },
        totalSum() {
            return this.detailLines.reduce((sum, row) => sum + (Number(row.TotalScore) || 0), 0);
        }
    },
    methods: {
        detailRowIndex(index) {
            return (this.detailPage - 1) * this.detailPageSize + index + 1;
        },
        onDetailSearch() {
            this.detailPage = 1;
        },
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },
        findAncestorDeptId(node, orgType) {
            let cur = node;
            const target = String(orgType || '').toLowerCase();
            while (cur) {
                const row = cur.data || cur;
                const type = String((row.DeptOrgType || row.deptOrgType) || '').toLowerCase();
                if (type === target) {
                    const id = row.value != null ? row.value : row.Id;
                    return id && String(id) !== '0' ? id : 0;
                }
                cur = cur.parent;
            }
            return 0;
        },
        async treeNodeClick(data, node) {
            if (!data) return;
            const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
            const deptId = data.value != null ? data.value : data.Id;
            if (!deptId || String(deptId) === '0') return;
            const schoolId = orgType === 'school' || orgType === 'affiliateschool'
                ? deptId
                : (this.findAncestorDeptId(node, 'school')
                    || this.findAncestorDeptId(node, 'affiliateschool')
                    || this.schoolId);
            if (!schoolId) return;
            const schoolChanged = String(this.schoolId) !== String(schoolId);
            this.schoolId = schoolId;
            this.departmentId = deptId;
            this.scopeLabel = data.label || data.Name || '';
            if (schoolChanged) {
                this.windowId = null;
                await this.loadWindows();
            }
            this.load(1);
        },
        formatScore(val) {
            if (val === null || val === undefined || val === '') return '-';
            const n = Number(val);
            if (isNaN(n)) return val;
            return Number.isInteger(n) ? String(n) : n.toFixed(2);
        },
        async loadWindows() {
            const res = await getClassAffairLedgerWindows({ schoolId: this.schoolId });
            this.windows = res.data && res.data.success ? (res.data.response || []) : [];
        },
        async load(page) {
            this.page = page || 1;
            this.loading = true;
            try {
                const res = await getClassAffairLedgers({
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    windowId: this.windowId || 0,
                    page: this.page,
                    intPageSize: this.pageSize
                });
                if (res.data && res.data.success) {
                    const body = res.data.response || {};
                    this.rows = body.data || [];
                    this.total = body.dataCount || 0;
                } else {
                    this.rows = [];
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            } finally {
                this.loading = false;
            }
        },
        async openDetail(row) {
            const res = await getClassAffairLedgerDetail({ schoolId: this.schoolId, ledgerId: row.Id });
            if (res.data && res.data.success) {
                this.detail = res.data.response;
                this.detailKeyword = '';
                this.detailPage = 1;
                this.detailPageSize = 20;
                this.detailVisible = true;
            } else {
                this.$message.error((res.data && res.data.msg) || '加载失败');
            }
        },
        async handleExport() {
            if (!this.schoolId) {
                this.$message.warning('请先选择部门');
                return;
            }
            this.exporting = true;
            try {
                const res = await exportClassAffairLedgers({
                    schoolId: this.schoolId,
                    departmentId: this.departmentId || 0,
                    windowId: this.windowId || 0
                });
                const blob = res.data;
                if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                    const text = await blob.text();
                    let msg = '导出失败';
                    try {
                        const json = JSON.parse(text);
                        msg = json.msg || msg;
                    } catch (err) { /* ignore */ }
                    this.$message.error(msg);
                    return;
                }
                if (!blob || blob.size === 0) {
                    this.$message.warning('没有数据可导出');
                    return;
                }
                const url = window.URL.createObjectURL(new Blob([blob], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }));
                const link = document.createElement('a');
                link.href = url;
                link.download = `德育台账_${new Date().toISOString().slice(0, 10)}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
                this.$message.success('导出成功');
            } catch (e) {
                this.$message.error('导出失败');
            } finally {
                this.exporting = false;
            }
        },
        openVoidDialog(row) {
            this.voidRow = row;
            this.voidReason = '';
            this.voidVisible = true;
        },
        async confirmVoid() {
            if (!this.voidRow) return;
            this.voiding = true;
            try {
                const res = await voidClassAffairLedger({
                    id: this.voidRow.Id,
                    schoolId: this.schoolId,
                    reason: this.voidReason
                });
                if (res.data && res.data.success) {
                    this.$message.success(res.data.msg || '作废成功');
                    this.voidVisible = false;
                    this.detailVisible = false;
                    this.load(this.page);
                } else {
                    this.$message.error((res.data && res.data.msg) || '作废失败');
                }
            } catch (e) {
                this.$message.error('作废失败');
            } finally {
                this.voiding = false;
            }
        }
    }
};
</script>

<style scoped>
.scope-hint { margin-bottom: 8px; color: #606266; font-size: 13px; }
.toolbar { margin-bottom: 8px; }
.ledger-table >>> .el-table__header,
.ledger-table >>> .el-table__body { width: 100% !important; }
.op-link { color: #409eff; }
.void-tip { margin: 0 0 12px; color: #606266; font-size: 13px; line-height: 1.5; }
.pager { margin-top: 12px; text-align: right; }
.empty-tip { padding: 80px 0; text-align: center; color: #909399; }
</style>

<style>
.class-affair-ledger-dialog .el-dialog__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 14px 20px 8px;
}
.class-affair-ledger-dialog .ledger-detail {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}
.class-affair-ledger-dialog .detail-head {
    margin-bottom: 12px;
    flex: 0 0 auto;
}
.class-affair-ledger-dialog .detail-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
}
.class-affair-ledger-dialog .detail-title__class {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 1.3;
}
.class-affair-ledger-dialog .detail-sub {
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
}
.class-affair-ledger-dialog .detail-void-meta {
    margin-top: 8px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
}
.class-affair-ledger-dialog .detail-void-meta__reason {
    margin-left: 8px;
    color: #606266;
}
.class-affair-ledger-dialog .stat-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
    flex: 0 0 auto;
}
.class-affair-ledger-dialog .stat-card {
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 10px;
    padding: 10px 12px;
}
.class-affair-ledger-dialog .stat-card__label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 4px;
}
.class-affair-ledger-dialog .stat-card__value {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    line-height: 1.2;
}
.class-affair-ledger-dialog .stat-card.is-bonus .stat-card__value { color: #e6a23c; }
.class-affair-ledger-dialog .stat-card.is-total .stat-card__value { color: #67c23a; }
.class-affair-ledger-dialog .is-voided .stat-card {
    background: #fafafa;
    border-color: #f0f0f0;
}
.class-affair-ledger-dialog .is-voided .stat-card__value,
.class-affair-ledger-dialog .is-voided .stat-card.is-bonus .stat-card__value,
.class-affair-ledger-dialog .is-voided .stat-card.is-total .stat-card__value {
    color: #c0c4cc;
}
.class-affair-ledger-dialog .is-voided .detail-table {
    color: #909399;
}
.class-affair-ledger-dialog .detail-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    flex: 0 0 auto;
}
.class-affair-ledger-dialog .detail-toolbar__hint {
    color: #909399;
    font-size: 12px;
}
.class-affair-ledger-dialog .detail-table-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}
.class-affair-ledger-dialog .detail-table {
    width: 100%;
}
.class-affair-ledger-dialog .detail-pager {
    margin-top: 10px;
    text-align: right;
    flex: 0 0 auto;
}
.class-affair-ledger-dialog .score-bonus { color: #e6a23c; font-weight: 600; }
.class-affair-ledger-dialog .score-total { color: #67c23a; font-weight: 600; }
@media screen and (max-width: 900px) {
    .class-affair-ledger-dialog .stat-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
